// src/utils/dentallyAppointmentStatus.js
//
// Presentation mapping for an appointment's `dentally` summary
// (DentallyAppointmentLink.safe_summary(): appointment_id, creation_state,
// cancellation_state, reconciliation_required) or null for a CRM-only booking.
//
// This describes provider identity and creation/cancellation state that the
// API exposes TODAY. It deliberately never says "Synced" / "Up to date": a
// verified schedule comparison needs the booking-sync contract (last_checked_at,
// differences) which does not exist yet.
//
// It DOES report the one divergence the API now proves: `update_unsynced` is
// true whenever the CRM time has not been shown to be the provider's time, so a
// reschedule whose provider write failed stops looking identical to a healthy
// booking once its toast has gone.
export const DENTALLY_APPOINTMENT_PRESENTATION = {
  crmOnly: { key: 'crm_only', label: 'CRM only', tone: 'neutral', icon: 'mdi:database-outline',
    tooltip: 'This appointment exists in the CRM only. It is not booked in Dentally.' },
  creating: { key: 'creating', label: 'Dentally booking pending', tone: 'warning', icon: 'mdi:progress-clock',
    tooltip: 'The Dentally booking has not been confirmed yet.' },
  review: { key: 'review', label: 'Dentally needs review', tone: 'warning', icon: 'mdi:alert-circle-outline',
    tooltip: 'The Dentally outcome for this appointment is unknown and needs review. Do not rebook.' },
  cancelled: { key: 'cancelled', label: 'Cancelled in Dentally', tone: 'neutral', icon: 'mdi:calendar-remove-outline',
    tooltip: 'This appointment has been cancelled in Dentally.' },
  cancelPending: { key: 'cancel_pending', label: 'Dentally cancellation pending', tone: 'warning', icon: 'mdi:progress-clock',
    tooltip: 'Cancellation in Dentally has not completed yet. Retry from the appointment menu.' },
  cancelling: { key: 'cancelling', label: 'Cancelling in Dentally', tone: 'warning', icon: 'mdi:progress-clock',
    tooltip: 'Cancellation in Dentally is in progress.' },
  linked: { key: 'linked', label: 'Dentally booking', tone: 'success', icon: 'mdi:calendar-check-outline',
    tooltip: 'This appointment is booked in Dentally. Booking status is not verified against Dentally from here yet.' },
  unsynced: { key: 'unsynced', label: 'Dentally not updated', tone: 'warning', icon: 'mdi:calendar-alert',
    tooltip: 'The CRM was updated but Dentally still shows the previous booking. Change it in Dentally, or edit this appointment again to retry.' },
};

export const getDentallyAppointmentPresentation = (dentally) => {
  const P = DENTALLY_APPOINTMENT_PRESENTATION;
  if (!dentally || typeof dentally !== 'object') return P.crmOnly;
  const { appointment_id: appointmentId, creation_state: creationState, cancellation_state: cancellationState } = dentally;
  if (cancellationState === 'CANCELLED') return P.cancelled;
  if (creationState === 'UNKNOWN' || cancellationState === 'UNKNOWN' || dentally.reconciliation_required === true) return P.review;
  if (creationState === 'IN_PROGRESS' || appointmentId == null) return P.creating;
  if (cancellationState === 'PENDING') return P.cancelPending;
  if (cancellationState === 'IN_PROGRESS') return P.cancelling;
  // Last, so a cancel in flight and an unresolved creation still win: those
  // describe the booking's existence, this only describes its time.
  if (dentally.update_unsynced === true) return P.unsynced;
  return P.linked;
};

/** Support reference — shown on demand (tooltip), never as the headline. */
export const getDentallyAppointmentReference = (dentally) =>
  (dentally?.appointment_id != null && dentally.appointment_id !== '' ? `Dentally appointment #${dentally.appointment_id}` : null);
